import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchThemeCategories,
  fetchThemeCountsByCategory,
  createThemeCategory,
  updateThemeCategoryTitle,
  updateThemeCategoryReference,
  deleteThemeCategory,
  readReferenceFile,
  CATEGORY_LANGUAGE_LABELS,
  type ThemeCategory,
  type CategorySourceLanguage,
} from '@/lib/themeCategories';

export default function ThemeCategoriesPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [categories, setCategories] = useState<ThemeCategory[]>([]);
  const [themeCounts, setThemeCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Champs du panneau d'édition
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [fileName, setFileName] = useState('');
  const [language, setLanguage] = useState<CategorySourceLanguage>('auto');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Création
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);

  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [data, counts] = await Promise.all([
        fetchThemeCategories(),
        fetchThemeCountsByCategory(),
      ]);
      setCategories(data);
      setThemeCounts(counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selected = useMemo(
    () => categories.find((c) => c.id === selectedId) || null,
    [categories, selectedId],
  );

  const openEditor = (cat: ThemeCategory) => {
    setSelectedId(cat.id);
    setTitle(cat.titre || '');
    setInstructions(cat.reference_instructions || '');
    setFileName(cat.reference_file_name || '');
    setLanguage((cat.source_language as CategorySourceLanguage) || 'auto');
    setSaved(false);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      if (title.trim() !== selected.titre) {
        await updateThemeCategoryTitle(selected.id, title);
      }
      await updateThemeCategoryReference(selected.id, instructions, fileName, language);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur d\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleReferenceFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      const content = await readReferenceFile(file);
      setInstructions(content);
      setFileName(file.name);
    } catch {
      setError('Impossible de lire le fichier de référence.');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    setError('');
    try {
      const created = await createThemeCategory(newTitle);
      setNewTitle('');
      setShowCreate(false);
      await load();
      openEditor(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de création');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (deletingId !== id) {
      setDeletingId(id);
      setTimeout(() => setDeletingId((prev) => (prev === id ? null : prev)), 2500);
      return;
    }
    setDeletingId(null);
    setError('');
    try {
      await deleteThemeCategory(id);
      if (selectedId === id) setSelectedId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de suppression');
    }
  };

  const filtered = categories.filter((c) =>
    c.titre.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <button
            onClick={() => navigate('/superadmin/themes')}
            className="flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer mb-2"
          >
            <i className="ri-arrow-left-line"></i>
            Retour aux thèmes
          </button>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Catégories de thèmes</h2>
          <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
            Chaque catégorie porte un <strong className="text-foreground-700">fichier de référence</strong> : les instructions transmises à l&apos;IA lors de la conversion d&apos;un thème de ce type.
          </p>
        </div>
        <button
          onClick={() => { setShowCreate(true); setNewTitle(''); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-foreground-950 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
        >
          <i className="ri-add-line"></i>
          Nouvelle catégorie
        </button>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
          <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
          <span className="text-xs text-red-700">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Colonne gauche : liste */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex flex-col">
          <div className="relative mb-3">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une catégorie..."
              className="w-full h-10 pl-9 pr-3 rounded-md border border-background-200/70 bg-background-100/50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <i className="ri-loader-4-line animate-spin text-foreground-400 text-xl"></i>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10">
              <i className="ri-folder-line text-3xl text-foreground-300 block mb-2"></i>
              <p className="text-xs text-foreground-500">Aucune catégorie</p>
            </div>
          ) : (
            <div className="space-y-1.5 overflow-y-auto pr-1">
              {filtered.map((cat) => {
                const isSelected = cat.id === selectedId;
                const hasRef = !!(cat.reference_instructions && cat.reference_instructions.trim());
                const count = themeCounts[String(cat.id)] || 0;
                return (
                  <button
                    key={cat.id}
                    onClick={() => openEditor(cat)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors cursor-pointer ${
                      isSelected
                        ? 'border-foreground-950 bg-background-100/70'
                        : 'border-background-200/70 bg-background-50 hover:border-background-300/60'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <i className={`${isSelected ? 'ri-folder-open-fill text-foreground-900' : 'ri-folder-line text-foreground-400'} text-sm flex-shrink-0`}></i>
                      <span className="text-xs font-medium text-foreground-900 truncate flex-1">{cat.titre}</span>
                      <span
                        className="text-[9px] px-1.5 py-0.5 rounded-full flex-shrink-0 bg-background-100 text-foreground-500"
                        title={`${count} thème(s) dans cette catégorie`}
                      >
                        {count}
                      </span>
                      {hasRef ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full flex-shrink-0 bg-accent-100 text-accent-700" title="Fichier de référence défini">Réf.</span>
                      ) : (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full flex-shrink-0 bg-amber-100 text-amber-700 whitespace-nowrap" title="Aucune référence définie pour cette catégorie">Réf. manquante</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Colonne droite : édition */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
          {!selected ? (
            <div className="h-full min-h-[300px] flex items-center justify-center">
              <div className="text-center">
                <i className="ri-book-open-line text-4xl text-foreground-300 block mb-3"></i>
                <p className="text-sm text-foreground-600 font-medium">Sélectionne une catégorie</p>
                <p className="text-xs text-foreground-400 mt-1">pour gérer son fichier de référence</p>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <i className="ri-book-open-line text-foreground-500"></i>
                  <span className="text-sm font-semibold text-foreground-900 truncate">{selected.titre}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 bg-background-100 text-foreground-500" title="Thèmes rattachés à cette catégorie">
                    <i className="ri-palette-line mr-0.5"></i>
                    {themeCounts[String(selected.id)] || 0} thème(s)
                  </span>
                  {!(selected.reference_instructions && selected.reference_instructions.trim()) && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 bg-amber-100 text-amber-700" title="Aucune référence définie">
                      <i className="ri-alert-line mr-0.5"></i>
                      Réf. manquante
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(selected.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    deletingId === selected.id
                      ? 'bg-red-500 text-white'
                      : 'bg-background-100 text-red-500 hover:bg-red-50 border border-background-200/70'
                  }`}
                >
                  <i className={deletingId === selected.id ? 'ri-check-line' : 'ri-delete-bin-line'}></i>
                  {deletingId === selected.id ? 'Confirmer' : 'Supprimer'}
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nom de la catégorie</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Langage source</label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value as CategorySourceLanguage)}
                    className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 cursor-pointer"
                  >
                    {(Object.keys(CATEGORY_LANGUAGE_LABELS) as CategorySourceLanguage[]).map((key) => (
                      <option key={key} value={key}>{CATEGORY_LANGUAGE_LABELS[key]}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Fichier de référence */}
              <div className="p-4 rounded-lg border border-accent-200/60 bg-accent-50/50">
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <i className="ri-file-text-line text-accent-600"></i>
                    <span className="text-xs font-semibold text-foreground-700 uppercase tracking-wider">Fichier de référence</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {fileName && (
                      <span className="text-[10px] font-mono bg-background-50 border border-background-200/70 px-2 py-1 rounded-full text-foreground-600 max-w-[180px] truncate">
                        {fileName}
                      </span>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".txt,.md,.markdown,.html,.json,.css"
                      onChange={handleReferenceFile}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground-950 text-background-50 text-xs font-medium whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
                    >
                      <i className="ri-upload-cloud-line"></i>
                      Importer un fichier
                    </button>
                  </div>
                </div>
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={14}
                  spellCheck={false}
                  placeholder={'Décris précisément les instructions à transmettre à l\'IA pour convertir un thème de cette catégorie.\n\nEx. :\n- Structure de pages attendue (accueil, produits, à propos...)\n- Palette et typographie à appliquer\n- Sections obligatoires\n- Ton éditorial et style visuel'}
                  className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 focus:ring-1 focus:ring-accent-300 resize-none font-mono leading-relaxed"
                  style={{ tabSize: 2 }}
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-[10px] text-foreground-400">
                    Ces instructions seront envoyées à l&apos;IA avec le thème à convertir.
                  </p>
                  <span className="text-[10px] text-foreground-400 font-mono">{instructions.length} car.</span>
                </div>
              </div>

              <button
                onClick={handleSave}
                disabled={saving || !title.trim()}
                className="w-full h-11 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <><i className="ri-loader-4-line animate-spin text-xs"></i>Enregistrement...</>
                ) : saved ? (
                  <><i className="ri-check-line"></i>Enregistré !</>
                ) : (
                  <><i className="ri-save-line"></i>Enregistrer la catégorie</>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal création */}
      {showCreate && (
        <>
          <div className="fixed inset-0 bg-black/30 z-50" onClick={() => setShowCreate(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm bg-background-50 rounded-lg border border-background-200/70 p-5 shadow-xl">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-folder-add-line text-foreground-500"></i>
              <h3 className="text-sm font-semibold text-foreground-950">Nouvelle catégorie</h3>
            </div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nom</label>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
              placeholder="Ex. Boutique de bijoux"
              autoFocus
              className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
            />
            <div className="flex items-center justify-end gap-3 mt-5">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !newTitle.trim()}
                className="px-5 py-2 rounded-full text-sm font-semibold bg-foreground-950 text-background-50 whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {creating ? <><i className="ri-loader-4-line animate-spin text-xs"></i>Création...</> : 'Créer'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}